import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import { Box, Button, Grid, Step, StepButton, Stepper } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { FormContext, useForm } from 'react-hook-form'
import { isEmpty } from 'lodash'

import { LoadingFeedback } from 'components'
import { GeneralInfo, Purposes, RiskAnalysis, Summary } from './components'

import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import { generalInfoSchema, guestGeneralInfoSchema } from './stepSchemas'
import { buildDefaultValues } from './formMappers'

const {
  FORM_STEPS: DEFAULT_STEPS,
  PURPOSES_STEP,
  STEPS_WITH_SUBMIT,
  STEP_FIELDS,
} = constants.aiInventory

const AiInventoryForm = ({
  mode = 'new',
  aiInventory = null,
  steps = DEFAULT_STEPS,
  guestMode = false,
  hideDepartment = false,
  currentDepartmentId = null,
  toolsService = null,
  departmentsService = null,
  purposesService = null,
  onStepSubmit,
  onBack,
  onPersonalDataChange,
  onAiInventoryRefresh,
  defaultStep = 0,
}) => {
  const snackbar = useSnackbar()
  const isShow = mode === 'show'
  const isEdit = mode === 'edit' || mode === 'show'
  const [activeStep, setActiveStep] = useState(defaultStep)
  const [hasPurposes, setHasPurposes] = useState(false)
  const [maxReachedStep, setMaxReachedStep] = useState(
    isEdit ? steps.length - 1 : defaultStep,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [noEligibleDepartments, setNoEligibleDepartments] = useState(false)
  const [calculatedRisk, setCalculatedRisk] = useState(
    aiInventory?.risk || null,
  )

  const formMethods = useForm({
    validationSchema: guestMode ? guestGeneralInfoSchema : generalInfoSchema,
    defaultValues: buildDefaultValues(aiInventory, { guestMode }),
  })

  useEffect(() => {
    if (!aiInventory) return
    formMethods.reset(buildDefaultValues(aiInventory, { guestMode }))
    setCalculatedRisk(aiInventory.risk || null)
  }, [aiInventory]) // eslint-disable-line react-hooks/exhaustive-deps

  const advance = () => {
    setMaxReachedStep((previous) => Math.max(previous, activeStep + 1))
    setActiveStep((previous) => previous + 1)
  }

  const isLastStep = activeStep === steps.length - 1

  // Valida os campos e a finalidade da etapa. triggerValidation com schema
  // retorna a validade do FORM inteiro (RHF v5), então checamos apenas os
  // erros dos campos da etapa em questão.
  const validateStep = async (stepIndex) => {
    const fieldsToValidate = STEP_FIELDS[stepIndex]
    if (!isEmpty(fieldsToValidate)) {
      await formMethods.triggerValidation(fieldsToValidate)
      if (fieldsToValidate.some((field) => formMethods.errors[field])) {
        return false
      }
    }

    if (stepIndex === PURPOSES_STEP && !hasPurposes) {
      snackbar.open({
        message: 'Adicione ao menos uma finalidade para continuar.',
        variant: 'error',
      })
      return false
    }

    return true
  }

  // Valida e, quando aplicável, persiste a etapa. Retorna se pode prosseguir.
  const commitStep = async (stepIndex) => {
    if (!(await validateStep(stepIndex))) return false
    if (!STEPS_WITH_SUBMIT.has(stepIndex)) return true

    setIsSaving(true)
    try {
      const result = await onStepSubmit(stepIndex, formMethods.getValues())
      if (result?.risk) setCalculatedRisk(result.risk)
      return true
    } catch {
      return false // snackbar exibido pelo parent
    } finally {
      setIsSaving(false)
    }
  }

  const handleNext = async () => {
    if (isShow) {
      isLastStep ? onBack() : advance()
      return
    }

    if (isLastStep) {
      if (await validateStep(activeStep)) onBack()
      return
    }

    if (await commitStep(activeStep)) advance()
  }

  // Navegação pelo Stepper: voltar é livre, mas avançar valida (e persiste)
  // cada etapa intermediária, parando na primeira inválida — assim não é
  // possível pular para o Resumo sem preencher as etapas obrigatórias.
  const handleStepClick = async (targetStep) => {
    if (isShow || targetStep <= activeStep) {
      setActiveStep(targetStep)
      return
    }

    for (let step = activeStep; step < targetStep; step++) {
      if (!(await commitStep(step))) {
        setActiveStep(step)
        return
      }
    }
    setActiveStep(targetStep)
  }

  const handleBack = () => {
    if (activeStep === 0) onBack()
    else setActiveStep((previous) => previous - 1)
  }

  const aiInventoryId = aiInventory?.id || null
  const stepLabel = `Etapa ${activeStep + 1} de ${steps.length}`

  const handleRiskSaved = (updated) => {
    if (updated?.risk) setCalculatedRisk(updated.risk)
    if (onAiInventoryRefresh) onAiInventoryRefresh()
  }

  return (
    <FormContext {...formMethods}>
      <LoadingFeedback open={isSaving} />
      <Grid container spacing={3}>
        <Grid item xs={12} id="ai-inventory-form-step-2">
          <Stepper activeStep={activeStep} nonLinear>
            {steps.map((label, index) => (
              <Step key={label} completed={index < maxReachedStep}>
                <StepButton
                  id={`ai-inventory-form-step-${index + 3}`}
                  onClick={() => handleStepClick(index)}
                  disabled={index > maxReachedStep || isSaving}
                >
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Grid>

        {activeStep === 0 && noEligibleDepartments && (
          <Grid item xs={12}>
            <Alert variant="outlined" severity="warning">
              <strong>Nenhum departamento encontrado.</strong> Seu usuário não
              está vinculado a nenhum departamento, portanto não é possível
              cadastrar uma IA. Solicite a um responsável da empresa que vincule
              seu usuário a um departamento.
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <Box display={activeStep === 0 ? 'block' : 'none'}>
            <GeneralInfo
              isShow={isShow}
              hideDepartment={hideDepartment}
              currentDepartmentId={currentDepartmentId}
              guestMode={guestMode}
              toolsService={toolsService}
              departmentsService={departmentsService}
              stepLabel={stepLabel}
              onNoEligibleDepartmentsChange={setNoEligibleDepartments}
            />
          </Box>
          <Box display={activeStep === 1 ? 'block' : 'none'}>
            <Purposes
              aiInventoryId={aiInventoryId}
              isShow={isShow}
              purposesService={purposesService}
              stepLabel={stepLabel}
              onHasPurposesChange={setHasPurposes}
            />
          </Box>
          <Box display={activeStep === 2 ? 'block' : 'none'}>
            <RiskAnalysis
              isShow={isShow}
              aiInventoryId={aiInventoryId}
              guestMode={guestMode}
              calculatedRisk={calculatedRisk}
              onSaved={handleRiskSaved}
              onPersonalDataChange={onPersonalDataChange}
              stepLabel={stepLabel}
              isActive={activeStep === 2}
            />
          </Box>
          <Box display={activeStep === 3 ? 'block' : 'none'}>
            <Summary
              aiInventory={aiInventory}
              stepLabel={stepLabel}
              isActive={activeStep === 3}
              onRefresh={onAiInventoryRefresh}
              guestMode={guestMode}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            id="ai-inventory-form-step-7"
            display="flex"
            justifyContent="flex-end"
            gridGap={8}
          >
            <Button variant="outlined" onClick={handleBack} disabled={isSaving}>
              Voltar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={isSaving}
            >
              {isLastStep ? 'Concluir' : 'Avançar'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </FormContext>
  )
}

AiInventoryForm.propTypes = {
  mode: PropTypes.oneOf(['new', 'edit', 'show']),
  aiInventory: PropTypes.object,
  steps: PropTypes.arrayOf(PropTypes.string),
  guestMode: PropTypes.bool,
  hideDepartment: PropTypes.bool,
  currentDepartmentId: PropTypes.number,
  toolsService: PropTypes.func,
  departmentsService: PropTypes.func,
  purposesService: PropTypes.shape({
    get: PropTypes.func,
    create: PropTypes.func,
    destroy: PropTypes.func,
  }),
  onStepSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onPersonalDataChange: PropTypes.func,
  onAiInventoryRefresh: PropTypes.func,
  defaultStep: PropTypes.number,
}

export default AiInventoryForm
